import React, { useMemo } from 'react';

import {
  Alert,
  AutocompleteItem,
  Button,
  Group,
  LoadingOverlay,
  Modal,
  Stack,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMediaQuery } from '@mantine/hooks';
import { IconAlertTriangleFilled, IconUser } from '@tabler/icons-react';
import AthleteAutoComplete, {
  AthleteAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/AthleteAutoComplete';
import dayjs from 'dayjs';
import useAppointment from 'hooks/appointment/useAppointment';
import useCreateAthleteAppointment from 'hooks/athleteAppointment/useCreateAthleteAppointment';
import useAthletePackageAllLimitedCreditsUsed from 'hooks/athletePackage/useAthletePackageAllLimitedCreditsUsed';
import useLastCreditAlertBuilder from 'hooks/athletePackage/useLastCreditAlertBuilder';
import useEventAthleteHasValidCreditDispensation from 'hooks/event/useEventAthleteHasValidCreditDispensation';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { AthleteAppointmentWrite } from 'types';
import { LastCreditAlertContent } from 'utils/last-credit-alert';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

import { AddAthleteState } from '../types';

interface IModalCreateAthleteAppointmentProps {
  opened: boolean;
  handleClose: () => void;
  appointmentId?: string;
  type: AddAthleteState;
  setLastCreditAlert: React.Dispatch<React.SetStateAction<LastCreditAlertContent | null>>;
}

interface FormValues {
  athlete: AthleteAutoCompleteValue;
}

const ModalCreateAthleteAppointment = ({
  opened,
  appointmentId,
  handleClose,
  type,
  setLastCreditAlert,
}: IModalCreateAthleteAppointmentProps): JSX.Element => {
  const theme = useMantineTheme();
  // ENG-2318: on mobile, anchor the modal to the top and keep it open on
  // outside taps so the keyboard doesn't cover the athlete dropdown.
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm})`, true);

  const { successNotification, errorNotification } = notificationHandler();
  const staffTracking = useStaffTracking();
  const createAthleteAppointmentMutation = useCreateAthleteAppointment();
  const getAthletePackageAllLimitedCreditsUsed = useAthletePackageAllLimitedCreditsUsed();
  const buildLastCreditAlert = useLastCreditAlertBuilder('staff');

  const form = useForm<FormValues>({
    initialValues: {
      athlete: {
        athleteId: '',
        value: '',
        label: '',
        firstName: '',
      },
    },

    validate: {
      athlete: value => (value.athleteId ? null : 'Athlete required'),
    },
  });

  const { data, isLoading } = useAppointment(appointmentId);
  const {
    data: creditData,
    isLoading: isCreditDataLoading,
    isSuccess: isCreditDataSuccess,
  } = useEventAthleteHasValidCreditDispensation({
    eventId: appointmentId,
    eventType: 'appointment',
    athleteId: form.values.athlete.athleteId,
    enabled: !!form.values.athlete.athleteId,
  });

  const hasValidCredits = creditData?.data?.hasValidCredits ?? false;
  const isPastEvent =
    data?.data &&
    dayjs(`${data.data.startDate}T${data.data.startTime}`).add(data.data.duration, 'minute').isBefore();
  const isPastEventAndNoCredits = isPastEvent && !hasValidCredits;
  const isAttendAndNoCredits = type === 'attended' && !hasValidCredits;
  const isSaveButtonDisabled =
    isLoading || isCreditDataLoading || isPastEventAndNoCredits || isAttendAndNoCredits;

  const alertTitle = useMemo(() => {
    if (isCreditDataSuccess && creditData.data && !creditData.data.hasValidCredits) {
      // Has no valid credits
      let alert = 'The athlete does not have an available credit for this appointment.';
      if (type === 'reserved' && !isPastEvent) {
        return alert + ' This reservation will be uncredited.';
      }
      if (type === 'attended' && !isPastEvent) {
        return alert + ' Attendance is disabled.';
      }
      return alert;
    }

    return null;
  }, [type, isCreditDataSuccess, isPastEvent, creditData?.data]);

  const athletesWithRelationToClass = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.data.athleteAppointments.map(akk => akk.athlete.athleteId);
  }, [data]);

  const handleCloseAndReset = (): void => {
    handleClose();
    form.reset();
  };

  const handleAthleteChange = (item: AutocompleteItem | null): void => {
    const selectedAthlete = {
      athleteId: item ? item.athleteId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
      firstName: item ? item.firstName : '',
    };
    form.setFieldValue('athlete', selectedAthlete);
  };

  const handleCreate = (values: FormValues): void => {
    if (!!appointmentId && values.athlete.athleteId) {
      const payload: AthleteAppointmentWrite = {
        athleteAppointmentId: uuidv4(),
        isReserved: type === 'reserved',
        isCheckedIn: type === 'attended',
        appointment: appointmentId,
        athlete: values.athlete.athleteId,
      };
      createAthleteAppointmentMutation.mutate(payload, {
        onSuccess: response => {
          const enrollmentStatus = response.data.isUncreditedReservation ? ' (Uncredited).' : '.';
          const notificationDescription = `${values.athlete.label} has been successfully added to ${data?.data.appointmentType.name}${enrollmentStatus}`;

          successNotification('Successfully added athlete to appointment', notificationDescription);

          if (payload.isCheckedIn && response.data?.creditDispensation) {
            getAthletePackageAllLimitedCreditsUsed.mutate(
              { creditDispensation: response.data.creditDispensation },
              {
                onSuccess: res => {
                  const alert = buildLastCreditAlert(res.data, values.athlete.firstName);
                  if (alert) {
                    setLastCreditAlert(alert);
                  }
                },
                onError: error => {
                  errorNotification(
                    'Error to check if all limited credits used',
                    error.response?.data.message,
                  );
                },
              },
            );
          }

          if (type === 'reserved' && appointmentId && data?.data?.appointmentType?.name) {
            staffTracking.trackAthleteReserved(
              values.athlete.athleteId,
              values.athlete.label,
              appointmentId,
              data.data.appointmentType.name,
              'appointment',
            );
          }
          if (type === 'attended' && appointmentId && data?.data?.appointmentType?.name) {
            staffTracking.trackAthleteCheckedIn(
              values.athlete.athleteId,
              values.athlete.label,
              appointmentId,
              data.data.appointmentType.name,
              'appointment',
            );
          }
          handleCloseAndReset();
        },
        onError: error => {
          errorNotification('Failed to add athlete', error.response?.data.message);
        },
      });
    }
  };

  return (
    <>
      <Modal
        centered={isDesktop}
        closeOnClickOutside={isDesktop}
        trapFocus
        opened={opened}
        onClose={handleCloseAndReset}
        title={
          <Title order={4}>
            Add {type} athlete to {data?.data.appointmentType.name}
          </Title>
        }
        size='md'
      >
        <LoadingOverlay visible={isLoading} />
        <form onSubmit={form.onSubmit(handleCreate)}>
          <Stack>
            <Group grow align='top'>
              <AthleteAutoComplete
                label='Athlete'
                placeholder='Select an athlete'
                value={form.values.athlete}
                onChange={handleAthleteChange}
                error={form.errors.athlete}
                icon={<IconUser />}
                dropdownPosition='bottom'
                withinPortal
                zIndex={99999}
                athletesWithRelationToClassOrAppointment={athletesWithRelationToClass}
              />
            </Group>

            {!!alertTitle && (
              <Alert icon={<IconAlertTriangleFilled size='3rem' />} color='yellow' fw='500'>
                {alertTitle}
              </Alert>
            )}

            <Group position='right'>
              <Button
                variant='subtle'
                onClick={handleCloseAndReset}
                disabled={createAthleteAppointmentMutation.isLoading}
              >
                Cancel
              </Button>
              <Button
                loading={createAthleteAppointmentMutation.isLoading}
                disabled={isSaveButtonDisabled}
                type='submit'
              >
                Save
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
};

export default ModalCreateAthleteAppointment;
